import React, { use, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useForm, useFormState } from "react-final-form";
import { FormInput, FormToggle, FormSelect, FormDate, FormDateRelease, FormListControl, FormMultiSelect, } from "ui-kit/SmartForm";
import IntlStore from "stories/IntlStore";
import { FormValues } from "./types";
import { ArtistItem } from "components/NewRelease/ReleaseInfo/ArtistItem";
import { ToggleButtonChangeEvent } from "primereact/togglebutton";
import { AddArtistModal } from "./AddArtistModal";
import { useArtists } from "../hooks/useArtists";
import { useContributors } from "../hooks/useContributors";
import { useGenres } from "../hooks/useGenres";
import { useRelease } from "../hooks/useRelease";
import { useParams } from "react-router";
import { AddLabelModal } from "./AddԼabelModal";
import Trash from 'assets/trash.svg';
import { useLabels } from "components/Labels/hooks/useLabels";
import { FormDateYear } from "ui-kit/SmartForm/FormDateYear";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { FormMultiSelectGaner } from "ui-kit/SmartForm/FormMultiSelectganer";


interface FormContentProps {
  values: FormValues;
}

export const FormContent: React.FC<FormContentProps> = props => {
  const { id } = useParams()
  const { values } = props;
  const { change } = useForm<FormValues>();
  const [selectedStatus, setSelectedStatus] = useState<string>('1');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(9999);
  const [isOpenArtistsModal, setIsOpenArtistsModal] = useState(false);
  const [isOpenLabelModal, setIsOpenLabelModal] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [search, setSearch] = useState<string>('');
  const [selectedArtist, setSelectedArtist] = useState<string>('');
  const [labelData, setLabelData] = useState('')
  const { upDateRole, reorderReleaseArtists } = useRelease()
  const { labels, createLabel, checkLabel, checkingStatus, checking, getLabels } = useLabels();
  const [plineDate, setPlineDate] = useState<Date | undefined>(undefined);
  const [clineDate, setClineDate] = useState<Date | undefined>(undefined);
  const [originalReleaseDate, setOriginalReleaseDate] = useState<Date | undefined>(undefined);
  const [releaseDateInitial, setReleaseDateInitial] = useState<Date | undefined>(undefined);
  const [ean, setEan] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenMain, setIsOpenMain] = useState(false);

  const additionalGenresInitial = useMemo(
    () => values?.genres?.filter(val => !val.is_primary).map(val => val.id) || [],
    [values?.genres]
  );
  const { artists, createArtist, creating: artistCreating } = useArtists(
    selectedStatus,
    currentPage,
    pageCount,
    search,
  );;
  const { creating: contributorCreating } = useContributors();
  const { genres } = useGenres();

  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  useEffect(() => {
    if (values.pline_date && new Date(values.pline_date).getTime() !== plineDate?.getTime()) {
      setPlineDate(new Date(values.pline_date));
    }
    if (values.cline_date && new Date(values.cline_date).getTime() !== clineDate?.getTime()) {
      setClineDate(new Date(values.cline_date));
    }
    if (values.original_release_date && new Date(values.original_release_date).getTime() !== originalReleaseDate?.getTime()) {
      setOriginalReleaseDate(new Date(values.original_release_date));
    }
    if (!values.original_release_date && originalReleaseDate) {
      // allow clearing original release date in UI without snapping back to server-provided initial value
      setOriginalReleaseDate(undefined);
    }
    if (values.release_date && new Date(values.release_date).getTime() !== releaseDateInitial?.getTime()) {
      setReleaseDateInitial(new Date(values.release_date));
    }
    if (values.ean && values.ean !== ean) {
      setEan(values.ean);
    }
  }, [values]);


  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setSearch(selectedArtist);
    }, 300);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [selectedArtist]);


  useEffect(() => {
    if (!values?.label) return;

    let labelName = "";

    if (typeof values.label === "string") {
      const found = labels?.find(val => String(val.id) === String(values.label));
      if (found) {
        labelName = found.name;
      } else if (!isNaN(+values.label)) {
        labelName = "";
      } else {
        labelName = values.label;
      }
    } else {
      labelName = values.label?.name || "";
    }

    if (labelData !== labelName) {
      setLabelData(labelName);
    }

    // Only auto-fill PLine/CLine from label when the section is enabled.
    // Otherwise toggle-off would immediately get "filled" again and look enabled.
    if (labelName && values?.hasPLineCLine) {
      if (!values.pline_value) change("pline_value", labelName);
      if (!values.cline_value) change("cline_value", labelName);
    }

    
  }, [values?.label, labels, labelData, values?.hasPLineCLine, values.pline_value, values.cline_value, change]);

  // Requirement:
  // - When enabling Edit PLine/CLine: auto-fill P/C-line text from Label immediately.
  // - When disabling: clear values to null immediately (so UI + submit are consistent).
  useEffect(() => {
    // Only react to explicit boolean values; don't auto-clear during initial undefined state.
    if (values?.hasPLineCLine === false) {
      // Clear fields right away when toggle OFF
      if (values?.pline_value != null) change("pline_value", null as any);
      if (values?.cline_value != null) change("cline_value", null as any);
      if (values?.pline_date != null) change("pline_date", null as any);
      if (values?.cline_date != null) change("cline_date", null as any);
      return;
    }

    if (values?.hasPLineCLine !== true) return;

    // Toggle ON: fill from Label if empty
    const labelName = labelData || "";
    if (!labelName) return;

    const pl = String(values?.pline_value ?? "").trim();
    const cl = String(values?.cline_value ?? "").trim();
    if (!pl) change("pline_value", labelName);
    if (!cl) change("cline_value", labelName);
  }, [
    values?.hasPLineCLine,
    labelData,
    values?.pline_value,
    values?.cline_value,
    values?.pline_date,
    values?.cline_date,
    change
  ]);


  
  // Determine if PLine/CLine text is the same as label text at initial load
  const labelNameForToggle = useMemo(() => {
    if (!values?.label) return "";

    if (typeof values.label === "string") {
      const found = labels?.find(val => String(val.id) === String(values.label));
      if (found) return found.name;
      if (!isNaN(+values.label)) return "";
      return values.label;
    }

    return values.label?.name || "";
  }, [values?.label, labels]);

  // NOTE: we no longer auto-hide the section when text matches label.
  // If server provides any P/C-line data, user should see it.

  // Ensure exactly one primary (MAIN) artist is selected:
  // - If multiple MAIN/status==true -> keep the first, demote the rest to FEAT
  // - If none -> promote the first to MAIN
  useEffect(() => {
    if (!values.artists || !values.artists.length) return;

    const mains = values.artists
      .map((a, idx) => ({ a, idx }))
      .filter(({ a }) => a?.role === "MAIN" || a?.status === true);

    const firstMainIdx =
      mains.length > 0 ? mains[0].idx : (values.artists[0] ? 0 : -1);

    if (firstMainIdx < 0) return;

    const alreadySingleMain =
      mains.length === 1 &&
      values.artists.every((a, idx) =>
        idx === firstMainIdx ? a?.role === "MAIN" : a?.role !== "MAIN"
      );

    if (alreadySingleMain) return;

    const normalized = values.artists.map((a, idx) =>
      idx === firstMainIdx ? { ...a, role: "MAIN" } : { ...a, role: "FEAT" }
    );

    change("artists", normalized);
  }, [values.artists, change]);


  const primaryGenreInitial = useMemo(() => {
    return values?.genres?.find(val => val.is_primary)?.id;
  }, [values?.genres]);
  const stablePrimaryGenreInitial = useMemo(
    () => (primaryGenreInitial ? [Number(primaryGenreInitial)] : []),
    [primaryGenreInitial]
  );

  const stableAdditionalGenresInitial = useMemo(
    () => (additionalGenresInitial?.map(Number).filter(Boolean) || []),
    [additionalGenresInitial]
  );




  return (
    <Container onClick={() => { setIsOpen(false), setIsOpenMain(false) }}>
      <InfoInputsContainer>
        <FormListControl
          id="atrists"
          name="artists"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.Artist",
            defaultMessage: "Artists",
          })}
          noItemsLabel={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.Artist.NoArtists",
            defaultMessage: "No added artists"
          })}
          addItemLabel={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.Artist.AddArtists",
            defaultMessage: "No added artists"
          })}
          onAddItem={() => setIsOpenArtistsModal(true)}
          tooltip="Artist Name Rules
To ensure your release appears correctly on DSPs and in search:
✅ Enter one name per field and only artists you have a confirmed connection with.
✅ Use “Various Artists” only if your release has more than 4 artists.
🚫 Don’t use generic terms (e.g., Sex, Yoga, Workout, Meditation, Car Music, Christmas, Top Hits, Hip Hop, Instrumental, Orchestra, Singer) or descriptors like guitarist, saxophone, V/A, ft., feat.
🚫 Don’t imitate famous artists or brands, or use book titles, characters, movies, or misleading keywords.
🚫 No offensive, hateful, or discriminatory content.
"
          totalItems={values?.artists?.length}
          required
        >
          <Box>
            <DragDropContext
              onDragEnd={async (result) => {
                if (!result.destination) return;
                const newArtists = reorder(
                  values.artists,
                  result.source.index,
                  result.destination.index
                );
                const prevArtists = values.artists;
                change("artists", newArtists);
                try {
                  const orderedIds = newArtists
                    .map(a => Number(a.id))
                    .filter(Boolean);
                  await reorderReleaseArtists(Number(id), { "order": orderedIds });
                } catch (err) {
                  change("artists", prevArtists);
                }
              }}
            >
              <Droppable
                droppableId="artists-droppable"
                isDropDisabled={false}
                isCombineEnabled={false}
                ignoreContainerClipping={false}
                direction="horizontal"
                type="ARTIST"
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ display: "flex", flexWrap: "wrap", gap: "5px", width: "100%" }}
                  >
                    {values?.artists?.map((artist, index) => (
                      <Draggable
                        key={artist.id || index}
                        draggableId={String(artist.id || index)}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <ArtistItem
                              name={artist.name}
                              label={artist.title}
                              // Treat MAIN role as the active/primary toggle.
                              checked={artist.role === "MAIN"}
                              role={artist.role}
                              onChange={async (event: ToggleButtonChangeEvent) => {
                                // Keep server state consistent: exactly ONE MAIN.
                                const selectedId = artist?.id;
                                if (!selectedId) return;

                                const prevArtists = values.artists;
                                const updatedArtists = prevArtists.map((a) =>
                                  a.id === selectedId ? { ...a, role: "MAIN" } : { ...a, role: "FEAT" }
                                );
                                change("artists", updatedArtists);

                                try {
                                  // Promote selected
                                  await upDateRole(Number(id), Number(selectedId), "MAIN");
                                  // Demote any other MAIN on the server (backend doesn't always auto-demote)
                                  const toDemote = prevArtists
                                    .filter((a) => a?.id && a.id !== selectedId && a.role === "MAIN");
                                  await Promise.all(
                                    toDemote.map((a) => upDateRole(Number(id), Number(a.id), "FEAT"))
                                  );
                                } catch (err) {
                                  console.error("Failed to update artist roles:", err);
                                  change("artists", prevArtists);
                                }
                              }}
                              onRemove={() => {
                                change(
                                  "artists",
                                  values.artists.filter((a) => a.id !== artist.id)
                                );
                              }}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </FormListControl>
        <FormInput
          id="release-title"
          name="releaseTitle"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.ReleaseTitle",
            defaultMessage: "Release title"
          })}
          placeholder={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.ReleaseTitle.Placeholder",
            defaultMessage: "Enter the release title"
          })}
          tooltip="✏️ Release Title Rules
Enter the official name of your release.
✅ Guidelines:
• For Singles, the release title must exactly match the track title and version.
• For EPs/Albums, track titles may differ.
🚫 Do NOT include:
• Type labels: Album, Single, EP, Ringtone, Compilation
• Generic terms: Sex, Yoga, Workout, Meditation, Car Music, Christmas, Top Hits, Hip Hop, Orchestra
• Attributes: Karaoke, Cover, feat., prod. by, Clean, Original Mix, Dolby Atmos, etc.
• Versions/editions: Limited Edition, Vol.1, OST, Alert Tone, etc.
• Dates: 2023, 2024, Recorded Jan 2021"
          initialValue={values?.title || ''}
          required
        />
        <FormInput
          id="release-version"
          name="releaseVersion"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.ReleaseVersion",
            defaultMessage: "Release version"
          })}
          placeholder={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.ReleaseVersion.Placeholder",
            defaultMessage: "Enter the release version"
          })}
          tooltip="🟡 Release Version (optional)
Used to distinguish your release from the original (e.g., Live, Bonus Track, Radio Edit).
✅ Allowed examples:
Live, Deluxe, Acoustic, Instrumental, Bonus Track, Radio Edit, Edit, Remastered (with year, e.g., “Remastered 2025”)
🚫 Do NOT include:
• Type labels (Album, EP, Ringtone, Compilation)
• Repeating the title (e.g., Title – Title)
• Language names (e.g., Spanish Version), unless official
• ALL CAPS (e.g., LIVE, REMIX)
• The word “Version” (use Radio Edit, not Radio Edit Version)
• Promotional phrases, brands, characters, keyword stuffing, or offensive content"
          initialValue={values?.version || ''}
        />
        <FormListControl
          name="label"
          label="Label"
          noItemsLabel="No added label"
          addItemLabel="Add Label"
          onAddItem={() => setIsOpenLabelModal(true)}
          tooltip="🏷️ Label Name
Enter the official label name for this release.
✅ Guidelines:
• If you don’t have a label, use your artist name.
• Label names must stay consistent across releases.
• After submission, changing the label requires takedown and resubmission.
🚫 Do NOT include:
• DSP names (Spotify, Apple, TikTok, YouTube, etc.)
• Links, hashtags, handles (@, #, .com)
• Emojis or special characters (★, ™, !, %, &)
• Excessive ALL CAPS or all-lowercase
📏 Formatting:
Use standard capitalization (e.g., Sunset Sounds).
🧠 Your label name represents the copyright holder/distributor—spell it correctly and professionally.
"
          totalItems={values?.label && !isOpenLabelModal ? 1 : 0}
          isDisabled={!!values?.label && !isOpenLabelModal}
          required
        >
          {!isOpenLabelModal && values?.label ? (
            <LabelBox>
              {labelData ? labelData : String(values.label)}
              <span
                onClick={() => { change('label', null), change('pline_value', ''), change('cline_value', '') }}
                style={{ cursor: 'pointer' }}
              >
                <Trash />
              </span>
            </LabelBox>
          ) : null}
        </FormListControl>
      </InfoInputsContainer>
      <ToggleContainer>
        <FormToggle
          name="hasPLineCLine"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.EditPLineCLine",
            defaultMessage: "Edit PLine/CLine",
          })}
          tooltip="Change PLine / CLine"
          initialValue={(() => {
            // Drafts come from server inside `values`.
            // If server has ANY P/C-line data (text or dates) we should show the section.
            const hasText =
              (values?.pline_value != null && String(values.pline_value).trim() !== "") ||
              (values?.cline_value != null && String(values.cline_value).trim() !== "");
            const hasDates = values?.pline_date != null || values?.cline_date != null;
            return hasText || hasDates;
          })()}
        />
      </ToggleContainer>
      <AdditionalInfoContainer>
        {values.hasPLineCLine && (
          <>
            <FormDateYear
              name="pline_date"
              label="PLine"

              initialValue={plineDate}
            />
            <FormInput
              name="pline_value"
              label=""
              initialValue={values?.pline_value}
            />
            <FormDateYear
              name="cline_date"
              label="CLine"
              initialValue={clineDate}

            />
            <FormInput
              name="cline_value"
              label=""
              initialValue={values?.cline_value}
            />
          </>
        )}
        <FormDateRelease
          id="release-date"
          name="releaseDate"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.ReleaseDate",
            defaultMessage: "Release date"
          })}
          tooltip="Choose the publication date for your release.
You can select any future date. We recommend uploading at least 2 weeks in advance (ideally 1 month) to ensure simultaneous delivery across DSPs, maximize launch impact, and allow time for error checks and compliance reviews.
"
          initialValue={releaseDateInitial}
          placeholder="Select the date"
          required
          allowFuture={true}
        />
        <FormDateRelease
          name="originalReleaseDate"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.OriginalReleaseDate",
            defaultMessage: "Original release date"
          })}
          tooltip="Original release date - the date on which the release was first made available for usage on DSPs"
          initialValue={originalReleaseDate}
          placeholder="Select the date" 
          allowPast={true}
          clearable
          readOnlyInput
          />
      </AdditionalInfoContainer>
      <FormSelectBox>
        <PrimaryGenreCol onClick={e => { e.stopPropagation(), setIsOpen(false) }}>
          {/* {!values.main_genre && <MainBox>RAP Cloud rap</MainBox>} */}
          {/* <FormSelect
            id="primaryGenre"
            name="primaryGenre"
            tooltip="tooltip"
            items={genres?.map(genre => ({ value: genre.id, label: genre.name }))}
            label={`Primary Genre (${values?.primaryGenre ? 1 : 0}/1)`}
            initialValue={values?.genres?.find(val => val.is_primary)?.id}
            required
            border={true}
          /> */}

          <div style={{ height: isOpenMain ? "320px" : 'auto' }}>
            <FormMultiSelectGaner
              name="main_genre"
              tooltip="Track genres"
              genres={genres as any}
              label={`Primary Genre *`}
              selectionLimit={1}
              initialValue={stablePrimaryGenreInitial}
              isOpen={isOpenMain}
              setIsOpen={setIsOpenMain}
              placeholder="Select additional genres"

            />
          </div>
        </PrimaryGenreCol>
        <AdditionalGenresCol style={{ height: isOpen ? "320px" : 'auto' }} onClick={e => { e.stopPropagation(), setIsOpenMain(false) }}>

          <FormMultiSelectGaner
            name="additionalGenres"
            genres={genres as any}
            label={`Additional Genres`}
            selectionLimit={2}
            initialValue={stableAdditionalGenresInitial}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            placeholder="Select additional genres"
            tooltip={null}
          />
          {/* <FormMultiSelect
            name="additionalGenres"
            tooltip="tooltip"
            items={genres?.map(genre => ({ value: genre.id, label: genre.name }))}
            label={`Additional Genres (${values?.additionalGenres?.length || 0}/2)`}
            maxSelectedLabels={2}
            selectionLimit={2}
            initialValue={additionalGenresInitial}
          /> */}
        </AdditionalGenresCol>
      </FormSelectBox>
      <ToggleContainer>
        <FormToggle
          id="ean-upc"
          name="hasEANUPC"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.EANUPC",
            defaultMessage: "I have my own EAN/UPC",
          })}
          tooltip="I have my own EAN/UPC"
          initialValue={!!values?.ean || false}
        />
      </ToggleContainer>

      {values.hasEANUPC && (
        <EANUPCContainer>
          <FormInput
            name="ean"
            label="EAN / UPC"
            tooltip="EAN/UPC
A unique product code for your release — it cannot be changed or reused for other releases.
If this is the first time you publish the release:
Leave the field empty — we will generate a new EAN/UPC.
If you're transferring from another distributor:
Enter the existing EAN/UPC to keep your release history and stats.
Use the old code only if all track info and track count are identical.
⚠️ Make sure the code is correct. Once delivered, it cannot be edited — changes require a takedown.
Typical format: 1234567890123."
            initialValue={values?.ean ?? ""}
          />
        </EANUPCContainer>
      )}
      <AddArtistModal
        isOpen={isOpenArtistsModal}
        onHide={() => setIsOpenArtistsModal(false)}
        artists={artists}
        createArtist={createArtist}
        artistCreating={artistCreating}
        selectedArtist={selectedArtist}
        setSelectedArtist={setSelectedArtist}
      />
      <AddLabelModal
        checkingStatus={checkingStatus}
        check={checkLabel}
        createLabel={createLabel}
        checking={checking}
        getLabels={getLabels}
        labels={labels}
        label={values.label}
        name=""
        isOpen={isOpenLabelModal}
        onHide={() => setIsOpenLabelModal(false)}
        contributorCreating={contributorCreating}
      />
    </Container>
  );
};

const Container = styled.div``;

const InfoInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 17px;
  margin: 32px 0 0;
`
const ToggleContainer = styled.div`
  margin: 25px 0;
`
const AdditionalInfoContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px 20px;
  align-items: flex-end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`
const EANUPCContainer = styled(AdditionalInfoContainer)`
  margin-bottom: 25px;
`
const Box = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  width: 100%;
`
const LabelBox = styled.div`
  width: 160px;
  height: 50px;
  border:1px solid #A4A4A4;
  display:flex;
  justify-content: space-between;
  align-items: center;
  padding:0 6px 0 9px;
  border-radius:7px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0%;
  vertical-align: middle;
  color:#6777FF;
`
const FormSelectBox = styled.div`
  width:100%;
  display:flex;
  gap:5px;
  padding-top:20px;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 14px;
  }
`

const PrimaryGenreCol = styled.div`
  width: 40%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AdditionalGenresCol = styled.div`
  width: 60%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;
const MainBox = styled.div`
  position: absolute;
  width: 72px;
  height: 33px;
  border-radius: 6px;
  font-family: SF Pro Display;
  font-weight: 400;
  font-style: Regular;
  font-size: 11px;
  leading-trim: NONE;
  line-height: 15px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #FFFFFF;
  background:#6777FF80;
  padding:3px 18px 2px 7px;
  z-index:999;
  top:53px;
  left:3px;
`



